import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAppSettings } from '@/context/app-settings';
import { useToast } from '@/hooks/use-toast';
import { Palette } from 'lucide-react';

export function BackgroundColorPicker() {
  const { settings, saving: contextSaving, saveSettings } = useAppSettings();
  const { toast } = useToast();
  const [backgroundColor, setBackgroundColor] = useState('#ffffff'); // Default to white instead of empty string
  const [saving, setSaving] = useState(false);

  // Load current background color from settings
  useEffect(() => {
    console.log('🎨 [BackgroundColorPicker] Settings updated:', settings);
    
    if (settings?.theme?.app_background_color) {
      console.log('🎨 [BackgroundColorPicker] Loading background color from settings:', settings.theme.app_background_color);
      setBackgroundColor(settings.theme.app_background_color);
    } else {
      // Keep the default white color if no custom background is set
      console.log('🎨 [BackgroundColorPicker] No custom background color in settings, keeping default');
    }
  }, [settings]);

  const handleColorChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newColor = event.target.value;
    console.log('🎨 [BackgroundColorPicker] Color changed to:', newColor);
    setBackgroundColor(newColor);
    // Note: Not applying directly to body - let the global hook handle it after save
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      console.log('🎨 [BackgroundColorPicker] Saving background color:', backgroundColor);
      
      // Apply the background color immediately via CSS custom property
      document.documentElement.style.setProperty('--app-background-color', backgroundColor);
      console.log('🎨 [BackgroundColorPicker] Applied CSS custom property directly');
      
      // Also save to database for persistence
      const updatedSettings = {
        ...settings,
        theme: {
          ...settings?.theme,
          app_background_color: backgroundColor
        }
      };

      const success = await saveSettings(updatedSettings);
      
      if (success) {
        toast({
          title: "Background Color Saved",
          description: "The background color has been updated successfully.",
        });
      } else {
        throw new Error('Failed to save settings');
      }
    } catch (error) {
      console.error('🎨 [BackgroundColorPicker] Error:', error);
      toast({
        title: "Error",
        description: "Failed to save background color.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Palette className="h-4 w-4" />
          Background Color
        </CardTitle>
        <CardDescription>
          Choose a background color for your application
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <label htmlFor="background-color" className="text-sm font-medium">
              Color:
            </label>
            <input
              id="background-color"
              type="color"
              value={backgroundColor || '#ffffff'}
              onChange={handleColorChange}
              className="w-12 h-8 rounded border border-border cursor-pointer"
              aria-label="Select background color"
            />
          </div>
          <div className="flex-1">
            <p className="text-sm text-muted-foreground">
              Current: {backgroundColor}
            </p>
          </div>
        </div>
        
        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={saving || contextSaving}>
            {(saving || contextSaving) ? 'Saving...' : 'Save Color'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}